import { FileText, Film, Loader2, Music, Paperclip, Send, X } from "lucide-react";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { CONVERSATION_MEDIA_ACCEPT, conversationMediaKind } from "@/lib/conversations";

/** Caja de respuesta "activa" (permiso ok, ventana de 24h abierta, no
 *  cerrada): textarea + botón enviar, con Intro-para-enviar / Shift+Intro
 *  para salto de línea. Compartida entre el inbox del inquilino y el inbox
 *  de soporte de plataforma. Los estados DESACTIVADOS (cerrada, fuera de
 *  ventana, sin permiso) tienen copy propio de cada inbox y se pintan aparte
 *  en cada página, no aquí.
 *
 *  Con `onAttachmentChange` aparece el clip para adjuntar una foto, un vídeo,
 *  un audio o un documento (22-sep-2026): el texto escrito viaja como pie
 *  (salvo en audios, que WhatsApp manda sin texto). La página valida el
 *  archivo y decide cómo enviarlo; aquí solo se elige y se previsualiza. */
export default function ConversationComposer({
  value,
  onChange,
  onSubmit,
  sending,
  placeholder = "Escribe tu respuesta... (Intro para enviar)",
  attachment = null,
  onAttachmentChange,
}: {
  value: string;
  onChange: (value: string) => void;
  onSubmit: (e: FormEvent) => void;
  sending: boolean;
  placeholder?: string;
  attachment?: File | null;
  onAttachmentChange?: (file: File | null) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const kind = attachment ? conversationMediaKind(attachment) : null;

  useEffect(() => {
    if (!attachment || !attachment.type.startsWith("image/")) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(attachment);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [attachment]);

  const PreviewIcon = kind === "audio" ? Music : kind === "document" ? FileText : Film;

  return (
    <form
      onSubmit={onSubmit}
      className="flex-shrink-0 bg-white border-t border-gray-200 p-3 space-y-2"
    >
      {attachment && (
        <div className="flex items-center gap-3 p-2 rounded-lg border border-gray-200 bg-gray-50">
          {previewUrl ? (
            <img src={previewUrl} alt="" className="w-12 h-12 rounded object-cover flex-shrink-0" />
          ) : (
            <div className="w-12 h-12 rounded bg-gray-200 flex items-center justify-center flex-shrink-0">
              <PreviewIcon className="w-5 h-5 text-gray-500" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-800 truncate">{attachment.name}</p>
            <p className="text-xs text-gray-500">
              {(attachment.size / (1024 * 1024)).toFixed(1)} MB ·{" "}
              {kind === "audio" ? "los audios van sin texto" : "el texto irá como pie"}
            </p>
          </div>
          <button
            type="button"
            onClick={() => onAttachmentChange?.(null)}
            disabled={sending}
            aria-label="Quitar archivo"
            className="p-1.5 rounded-lg text-gray-500 hover:bg-gray-200 disabled:opacity-50"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      <div className="flex gap-2">
        {onAttachmentChange && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept={CONVERSATION_MEDIA_ACCEPT}
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null;
                // Vaciar para que elegir el mismo archivo otra vez vuelva a disparar onChange.
                e.target.value = "";
                if (file) onAttachmentChange(file);
              }}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending}
              aria-label="Adjuntar archivo"
              title="Adjuntar foto, vídeo, audio o documento"
              className="self-end p-2 rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-50 transition-colors"
            >
              <Paperclip className="w-5 h-5" />
            </button>
          </>
        )}
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            // Intro envía; Shift+Intro inserta salto de línea (estándar de chat)
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              e.currentTarget.form?.requestSubmit();
            }
          }}
          placeholder={
            kind === "audio"
              ? "WhatsApp no admite texto junto a un audio (Intro para enviar)"
              : attachment
                ? "Añade un texto (opcional)… (Intro para enviar)"
                : placeholder
          }
          disabled={kind === "audio"}
          rows={2}
          maxLength={attachment ? 1024 : 4096}
          className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none disabled:bg-gray-50"
        />
        <button
          type="submit"
          disabled={sending || (!value.trim() && !attachment)}
          className="self-end px-4 py-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {sending ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Send className="w-5 h-5" />
          )}
        </button>
      </div>
    </form>
  );
}
